import { Box, Container, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { colorTokens, layoutTokens, radiusTokens, typographyTokens } from '../app/designTokens';
import mafertSystemLogoWhite from '../assets/brand/mafert-system-logo-white.svg';
import { LanguageSwitcher } from './LanguageSwitcher';

const { semantic } = colorTokens;

/**
 * Shared public onboarding chrome (Login / Register / Launch status).
 * Full-width ~72px topbar: logo left, login hint + language right.
 */
export function PublicOnboardingShell({
  children,
  maxWidth = 'sm',
  maxWidthPx,
  title,
  subtitle,
  showLanguageSwitcher = true,
  showLoginAction = false,
  footer,
  testId = 'public-onboarding-shell',
}) {
  const { t } = useTranslation(['auth']);

  return (
    <Box
      data-testid={testId}
      sx={{
        minHeight: '100vh',
        bgcolor: semantic.bg.canvas,
        color: semantic.text.primary,
      }}
    >
      <Box
        component="header"
        data-testid="public-onboarding-topbar"
        sx={{
          height: layoutTokens.topbarHeight,
          minHeight: layoutTokens.topbarHeight,
          borderBottom: `1px solid ${semantic.border.subtle}`,
          bgcolor: semantic.bg.canvas,
          px: { xs: 2, md: 3 },
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ width: '100%', minWidth: 0 }}
        >
          <Box
            component={RouterLink}
            to="/login"
            sx={{ display: 'inline-flex', textDecoration: 'none', flexShrink: 0 }}
            aria-label="Mafert System"
          >
            <Box
              component="img"
              src={mafertSystemLogoWhite}
              alt="Mafert System"
              data-testid="public-onboarding-logo"
              sx={{
                width: 160,
                maxWidth: '100%',
                maxHeight: 40,
                height: 'auto',
                objectFit: 'contain',
                objectPosition: 'left center',
                display: 'block',
              }}
            />
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 1, sm: 2 }}
            flexWrap="wrap"
            useFlexGap
            justifyContent="flex-end"
            sx={{ minWidth: 0 }}
          >
            {showLoginAction ? (
              <Typography
                component="span"
                data-testid="public-onboarding-login-hint"
                sx={{
                  fontFamily: typographyTokens.fontFamily,
                  fontSize: { xs: 12, sm: 13 },
                  color: semantic.text.muted,
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  textAlign: 'right',
                }}
              >
                {t('auth:already_have_account')}{' '}
                <Box
                  component={RouterLink}
                  to="/login"
                  data-testid="public-onboarding-login-action"
                  sx={{
                    color: semantic.text.primary,
                    textDecoration: 'underline',
                    fontWeight: typographyTokens.fontWeightSemibold,
                  }}
                >
                  {t('auth:login')}
                </Box>
              </Typography>
            ) : null}
            {showLanguageSwitcher ? (
              <LanguageSwitcher variant="icon" size="small" />
            ) : null}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ py: { xs: 3, md: 5 }, px: 2 }}>
        <Container maxWidth={maxWidth} sx={maxWidthPx ? { maxWidth: maxWidthPx } : undefined}>
          <Stack spacing={3}>
            {(title || subtitle) ? (
              <Box>
                {title ? (
                  <Typography
                    component="h1"
                    sx={{
                      fontFamily: typographyTokens.fontFamilyDisplay,
                      fontWeight: typographyTokens.fontWeightBold,
                      fontSize: { xs: 24, md: 28 },
                      lineHeight: 1.25,
                      color: semantic.text.primary,
                      mb: subtitle ? 1 : 0,
                    }}
                  >
                    {title}
                  </Typography>
                ) : null}
                {subtitle ? (
                  <Typography
                    sx={{
                      fontFamily: typographyTokens.fontFamily,
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: semantic.text.secondary,
                    }}
                  >
                    {subtitle}
                  </Typography>
                ) : null}
              </Box>
            ) : null}

            <Box
              sx={{
                bgcolor: semantic.bg.surface,
                border: `1px solid ${semantic.border.subtle}`,
                borderRadius: `${radiusTokens.card}px`,
                p: { xs: 2.5, md: 3 },
              }}
            >
              {children}
            </Box>

            {footer ? (
              <Box sx={{ color: semantic.text.muted, fontSize: 12 }}>{footer}</Box>
            ) : null}
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

export default PublicOnboardingShell;
